// src/components/dashboard/UpcomingBills.tsx 
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useUpcomingBills } from '../../hooks/useUpcomingBills';
import { useFormatting } from '../../context/FormattingContext';
import { formatDateInTimeZone, formatMoney } from '../../utils/formatting';

const UpcomingBills: React.FC = () => {
  const { upcoming, loading, error } = useUpcomingBills();
  const formatting = useFormatting();

  // 🧮 Calculate total due
  const totalAmount = upcoming.reduce((sum, bill) => sum + (bill.amount || 0), 0);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight="bold">📅 Upcoming Bills</Typography>

      {loading ? (
        <CircularProgress size={20} sx={{ mt: 1 }} />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : upcoming.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No upcoming bills.</Typography>
      ) : (
        <>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {upcoming.map(bill => {
              const dueDate = bill.date;

              if (!dueDate) {
                console.warn('⚠️ Skipping transaction with no valid `date` field:', bill);
                return null;
              }

              return (
                <Box key={bill._id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>{bill.description || bill.category || 'Untitled Bill'}</Typography>
                  <Typography>
                    {formatMoney(bill.amount, formatting)} due {formatDateInTimeZone(dueDate, {
                      locale: formatting.locale,
                      timeZone: formatting.timeZone,
                      dateStyle: 'medium',
                    })}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" fontWeight="medium">
            Total amount of your bills over the next 30 days: <strong>{formatMoney(totalAmount, formatting)}</strong>
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default UpcomingBills;
